import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FORM, LAYOUT_ITEM, QUOTATION_DTO_FORM } from 'constants/FormConstants';
import { RutValidator } from 'utils/RutValidator';
import * as yup from 'yup';
import { CreateFormLayout } from 'components/create-form/CreateFormLayout';
import { MultiFormCardHeader } from 'components/MultiFormCardHeader';
import { DATA_LAYERS } from 'constants/DataLayers';
import TagManager from 'react-gtm-module';
import { QuotationControllerApi } from 'client';
import { getTokenFromStorage } from '../../../utils/GetTokenFromStorage';

export interface QuotationSingleFormProps {
	dataLayerKey?: string;
	formData: any;
	setIdVehiculo: (v: string) => void;
	setData: (v: any) => void;
}

/** ────────────────────────────────────────────────────────────────────────────
 *  Small utility that remembers the previous value of whatever is passed in */
const usePrevious = <T,>(value: T) => {
	const ref = useRef<T>(value);
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};
// ──────────────────────────────────────────────────────────────────────────────

export const QuotationSingleForm = ({
																			dataLayerKey,
																			formData,
																			setIdVehiculo,
																			setData,
																		}: QuotationSingleFormProps) => {
	const token = getTokenFromStorage();
	const [vehicleTypes, setVehicleTypes] = useState<{ label: string; value: string }[]>([]);
	const [marcaOptions, setMarcaOptions] = useState<{ label: string; value: string }[]>([]);
	const [modeloOptions, setModeloOptions] = useState<{ label: string; value: string }[]>([]);
	const [regionOptions, setRegionOptions] = useState<{ label: string; value: string }[]>([]);
	const [comunaOptions, setComunaOptions] = useState<{ label: string; value: string }[]>([]);
	const [sucursalOptions, setSucursalOptions] = useState<{ label: string; value: string }[]>([]);

	useEffect(() => {
		if (dataLayerKey) {
			TagManager.dataLayer({ dataLayer: DATA_LAYERS.get(dataLayerKey) });
		}

		const controller = new QuotationControllerApi();
		controller.getTypeVehicleUsingGet(token).then((res) => {
			if (Array.isArray(res.data)) {
				setVehicleTypes(res.data.map((item) => ({ label: item, value: item })));
			}
		});
	}, [dataLayerKey, token]);

	/* -------------------------------------------------------------------------- */
	/* 2️⃣  Dynamic data: cascade + GET guarded by five key fields                */
	/* -------------------------------------------------------------------------- */
	const {
		vehicleType,
		vehicleBrand,
		vehicleValue,
		region,
		county,
	} = formData;

	/** Only track the fields that matter for the request */
	const queryParams = { vehicleType, vehicleBrand, vehicleValue, region, county };
	const prevQueryParams = usePrevious(queryParams);

	useEffect(() => {
		/* ----- A. Skip if nothing relevant changed ----------------------------- */
		if (
			prevQueryParams &&
			Object.entries(queryParams).every(
				([k, v]) => prevQueryParams[k as keyof typeof prevQueryParams] === v
			)
		) {
			return;
		}

		/* ----- B. Reset dependent dropdowns (single render) -------------------- */
		const updatedForm = { ...formData };

		if (vehicleType !== prevQueryParams?.vehicleType) {
			updatedForm[FORM.VEHICLE_BRAND] =
				updatedForm[FORM.VEHICLE_VALUE] =
					updatedForm[FORM.REGION] =
						updatedForm[FORM.COUNTY] =
							updatedForm[FORM.BRAND] =
								'';
		} else if (vehicleBrand !== prevQueryParams?.vehicleBrand) {
			updatedForm[FORM.VEHICLE_VALUE] =
				updatedForm[FORM.REGION] =
					updatedForm[FORM.COUNTY] =
						updatedForm[FORM.BRAND] =
							'';
		} else if (vehicleValue !== prevQueryParams?.vehicleValue) {
			updatedForm[FORM.REGION] = updatedForm[FORM.COUNTY] = updatedForm[FORM.BRAND] = '';
		} else if (region !== prevQueryParams?.region) {
			updatedForm[FORM.COUNTY] = updatedForm[FORM.BRAND] = '';
		} else if (county !== prevQueryParams?.county) {
			updatedForm[FORM.BRAND] = '';
		}

		if (JSON.stringify(updatedForm) !== JSON.stringify(formData)) {
			setData(updatedForm); // triggers a single re-render upstream
		}

		/* ----- C. Stop here until we have at least vehicleType ----------------- */
		if (!vehicleType) return;

		/* ----- D. Fetch dependent field values --------------------------------- */
		const controller = new QuotationControllerApi();
		controller
			.getDataFormsFieldsUsingGet(token, vehicleType, vehicleBrand, vehicleValue, region, county)
			.then(({ data }) => {
				/* idVehiculo */
				// @ts-ignore
				setIdVehiculo([...new Set(data.map((i) => i.id))][0]);

				/* helper to build <option>s */
				// @ts-ignore
				const makeOptions = (key: keyof (typeof data)[0]) =>
					// @ts-ignore
					[...new Set(data.map((i) => i[key]))].map((v) => ({
						label: String(v),
						value: String(v),
					}));

				if (vehicleType && !vehicleBrand) setMarcaOptions(makeOptions('marca'));
				if (vehicleBrand && !vehicleValue) setModeloOptions(makeOptions('modelo'));
				if (vehicleValue && !region) setRegionOptions(makeOptions('region'));
				if (region && !county) setComunaOptions(makeOptions('comuna'));
				if (county) setSucursalOptions(makeOptions('sucursal'));
			})
			.catch((err) => console.error('API Error:', err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehicleType, vehicleBrand, vehicleValue, region, county, token]);

	/* -------------------------------------------------------------------------- */
	/* 3️⃣  Form layout definition                                               */
	/* -------------------------------------------------------------------------- */
	const fields = useMemo(
		() => [
			{
				label: 'Nombre Completo',
				defaultValue: '',
				name: QUOTATION_DTO_FORM.FULL_NAME,
				type: LAYOUT_ITEM.TYPE.INPUT,
				placeholder: 'Ej: Juan Manuel',
				validation: yup
					.string()
					.required('Campo Requerido')
					.matches(
						/^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u,
						'Ingrese solo letras'
					),
			},
			{
				label: 'Rut',
				defaultValue: '',
				name: QUOTATION_DTO_FORM.RUT,
				type: LAYOUT_ITEM.TYPE.INPUT,
				placeholder: 'Ej: 12345678-9',
				mask: 'rut',
				validation: yup
					.string()
					.required('Campo Requerido')
					.min(8, 'Ingrese un RUT válido')
					.test('rut-valid', 'Ingrese un RUT válido', (value) =>
						value !== undefined ? RutValidator(value) : false
					),
			},
			{
				label: 'Correo electrónico',
				defaultValue: '',
				name: QUOTATION_DTO_FORM.EMAIL,
				type: LAYOUT_ITEM.TYPE.INPUT,
				placeholder: 'Ej: mail@mail.cl',
				validation: yup.string().email('Ingrese un email válido').required('Campo Requerido'),
			},
			{
				label: 'Teléfono',
				defaultValue: '',
				name: QUOTATION_DTO_FORM.TELEPHONE,
				type: LAYOUT_ITEM.TYPE.INPUT,
				prefix: '+56 ',
				placeholder: '9 8765 4321',
				mask: '# ### ### ##',
				validation: yup
					.number()
					.typeError('Ingrese un número válido')
					.min(111111111, 'Ingrese un número válido')
					.max(999999999, 'Ingrese un número válido')
					.notOneOf([999999999], 'Ingrese un número válido')
					.required('Campo Requerido'),
			},
			{
				defaultValue: '',
				type: LAYOUT_ITEM.TYPE.SELECT,
				label: 'Tipo de Vehículo',
				name: FORM.VEHICLE_TYPE,
				options: vehicleTypes,
				validation: yup.string().required('Campo Requerido'),
			},
			{
				defaultValue: '',
				type: LAYOUT_ITEM.TYPE.SELECT,
				label: 'Marca',
				name: FORM.VEHICLE_BRAND,
				options: marcaOptions,
				disabled: marcaOptions.length < 1,
				validation: yup.string().required('Campo Requerido'),
			},
			{
				defaultValue: '',
				type: LAYOUT_ITEM.TYPE.SELECT,
				label: 'Modelo',
				name: FORM.VEHICLE_VALUE,
				options: modeloOptions,
				disabled: modeloOptions.length < 1,
				validation: yup.string().required('Campo Requerido'),
			},
			{
				defaultValue: '',
				type: LAYOUT_ITEM.TYPE.SELECT,
				label: 'Región',
				name: FORM.REGION,
				options: regionOptions,
				disabled: regionOptions.length < 1,
				validation: yup.string().required('Campo Requerido'),
			},
			{
				defaultValue: '',
				type: LAYOUT_ITEM.TYPE.SELECT,
				label: 'Comuna',
				name: FORM.COUNTY,
				options: comunaOptions,
				disabled: comunaOptions.length < 1,
				validation: yup.string().required('Campo Requerido'),
			},
			{
				defaultValue: '',
				type: LAYOUT_ITEM.TYPE.SELECT,
				label: 'Sucursal',
				name: FORM.BRAND,
				options: sucursalOptions,
				disabled: sucursalOptions.length < 1,
				validation: yup.string().required('Campo Requerido'),
			},
		],
		[vehicleTypes, marcaOptions, modeloOptions, regionOptions, comunaOptions, sucursalOptions]
	);

	/* -------------------------------------------------------------------------- */
	/* 4️⃣  Render                                                               */
	/* -------------------------------------------------------------------------- */
	return (
		<div>
			<MultiFormCardHeader
				title="Información personal"
				body="Completa tus datos para que te podamos evaluar y contactar correctamente."
			/>
			<CreateFormLayout
				key={fields.length}
				fields={fields}
				watchField={[
					FORM.VEHICLE_TYPE,
					FORM.VEHICLE_BRAND,
					FORM.VEHICLE_VALUE,
					FORM.REGION,
					FORM.COUNTY,
				]}
			/>
		</div>
	);
};
